import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { askPlatformFaq, fetchFaqAiStatus, setFaqAiFeedback } from "../../../services/faqService";
import { getUserData } from "../../../services/apiService";
import { submitFeedbackToClickUp } from "../../../services/clickupService";
import { validateFaqAiQuestion } from "./faqAiQuestionGuard";
import FaqAskAnswerBody from "./faqAskAnswerFormat";
import FaqAssistMark, { FaqAssistAiIcon } from "./FaqAssistMark";
import { organizationCanSeeFaqTopic } from "../../../utils/orgModuleAccess";
import {
  buildAskRecentId,
  pushFaqRecent,
} from "./faqRecentActivity";

const ASSISTANT_LOADING_MIN_MS = 700;

function scrollThreadToLatest(threadEl) {
  if (!threadEl) return;
  threadEl.scrollTop = threadEl.scrollHeight;
}

/** Scroll the thread so the message's top is near the visible top (read from start). */
function scrollThreadToMessageStart(threadEl, messageEl) {
  if (!threadEl || !messageEl) return;
  const threadRect = threadEl.getBoundingClientRect();
  const msgRect = messageEl.getBoundingClientRect();
  const nextTop = threadEl.scrollTop + (msgRect.top - threadRect.top) - 8;
  threadEl.scrollTop = Math.max(0, nextTop);
}

function buildSupportTicketDraft({ moduleName, question, answer, whyNot }) {
  const moduleLabel = String(moduleName || "AQ Trials").trim() || "AQ Trials";
  const q = String(question || "").trim() || "(not recorded)";
  const a = String(answer || "").trim();
  const answerSnippet =
    a.length > 700 ? `${a.slice(0, 700)}…` : a || "(no answer text)";
  const why = String(whyNot || "").trim() || "(please add a short note)";

  return {
    subject: `AQ Assist – help needed (${moduleLabel})`,
    // Put the staff note first so it is obvious in ClickUp / email previews.
    description: [
      "Why this was not helpful:",
      why,
      "",
      `Module: ${moduleLabel}`,
      "",
      "What I asked AQ Assist:",
      q,
      "",
      "Answer I received:",
      answerSnippet,
      "",
      "Please advise on the correct steps.",
    ].join("\n"),
    whyNot: why,
  };
}

function FaqAskPanel({
  fallbackModules = [],
  initialModule = "",
  initialMessages = [],
  chatSessionId = "",
  onSessionChange,
  onUsageChange,
  onFeedbackChange,
  variant = "page",
  modulePicker = "dropdown",
}) {
  const session = getUserData() || {};
  const organizationId = session.OrganizationId || session.Organizationid || "";
  const staffId = session.StaffId || "";

  const [status, setStatus] = useState({
    loading: true,
    enabled: false,
    modules: [],
    message: "",
    dailyOrgCap: 50,
    dailyUserCap: 50,
    maxQuestionLength: 1000,
    maxQuestionLineBreaks: 6,
    orgRemaining: 0,
    userRemaining: 0,
  });
  const [moduleName, setModuleName] = useState(() =>
    String(initialModule || "").trim(),
  );
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState(() =>
    Array.isArray(initialMessages) ? initialMessages : [],
  );
  const [sending, setSending] = useState(false);
  const [localError, setLocalError] = useState("");
  const [feedbackBusyId, setFeedbackBusyId] = useState("");
  const [supportDraft, setSupportDraft] = useState(null);
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const threadRef = useRef(null);
  const lastBubbleRef = useRef(null);
  const supportNoteRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const prevSessionIdRef = useRef(chatSessionId);

  // Opened chat → bottom. New assistant reply → start of that response.
  useLayoutEffect(() => {
    if (status.loading) return undefined;

    const thread = threadRef.current;
    const sessionChanged = prevSessionIdRef.current !== chatSessionId;
    if (sessionChanged) {
      prevSessionIdRef.current = chatSessionId;
      prevMessageCountRef.current = 0;
    }

    // Thread not mounted yet (e.g. still choosing module) — do not mark as scrolled.
    if (!thread) return undefined;

    if (messages.length === 0) {
      prevMessageCountRef.current = 0;
      return undefined;
    }

    const prevCount = prevMessageCountRef.current;
    const grew = messages.length > prevCount;
    const openedChat = prevCount === 0 || sessionChanged;

    if (!openedChat && !grew) {
      prevMessageCountRef.current = messages.length;
      return undefined;
    }

    const lastMsg = messages[messages.length - 1];
    const scrollToResponseStart =
      grew && lastMsg?.role === "assistant" && !openedChat;

    const runScroll = () => {
      if (scrollToResponseStart) {
        scrollThreadToMessageStart(thread, lastBubbleRef.current);
      } else {
        scrollThreadToLatest(thread);
      }
    };

    runScroll();
    const raf1 = requestAnimationFrame(() => {
      runScroll();
      requestAnimationFrame(runScroll);
    });
    const t1 = window.setTimeout(runScroll, 50);
    const t2 = window.setTimeout(runScroll, 200);

    prevMessageCountRef.current = messages.length;
    return () => {
      cancelAnimationFrame(raf1);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [chatSessionId, messages, status.loading, moduleName]);

  const modules = useMemo(() => {
    const userData = getUserData();
    const seen = new Set();
    const list = [];
    // Prefer FAQ Topics (org-licensed modules only) — same list as the FAQ tree.
    const source =
      fallbackModules.length > 0 ? fallbackModules : status.modules || [];
    source.forEach((name) => {
      const trimmed = String(name || "").trim();
      const key = trimmed.toLowerCase();
      if (!trimmed || seen.has(key)) return;
      if (!organizationCanSeeFaqTopic(userData, trimmed)) return;
      seen.add(key);
      list.push(trimmed);
    });
    return list;
  }, [status.modules, fallbackModules]);

  const loadStatus = useCallback(async () => {
    const next = await fetchFaqAiStatus(organizationId, staffId);
    setStatus({
      loading: false,
      enabled: !!next.enabled,
      modules: next.modules || [],
      message: next.message || "",
      dailyOrgCap: next.dailyOrgCap,
      dailyUserCap: next.dailyUserCap,
      maxQuestionLength: next.maxQuestionLength,
      maxQuestionLineBreaks: next.maxQuestionLineBreaks,
      orgRemaining: next.orgRemaining,
      userRemaining: next.userRemaining,
    });
    return next;
  }, [organizationId, staffId]);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    const finishAfterMinDelay = async (apply) => {
      const waitMs = Math.max(
        0,
        ASSISTANT_LOADING_MIN_MS - (Date.now() - startedAt),
      );
      if (waitMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, waitMs));
      }
      if (!cancelled) apply();
    };

    (async () => {
      try {
        const next = await fetchFaqAiStatus(organizationId, staffId);
        await finishAfterMinDelay(() => {
          setStatus({
            loading: false,
            enabled: !!next.enabled,
            modules: next.modules || [],
            message: next.message || "",
            dailyOrgCap: next.dailyOrgCap,
            dailyUserCap: next.dailyUserCap,
            maxQuestionLength: next.maxQuestionLength,
            maxQuestionLineBreaks: next.maxQuestionLineBreaks,
            orgRemaining: next.orgRemaining,
            userRemaining: next.userRemaining,
          });
        });
      } catch (err) {
        await finishAfterMinDelay(() => {
          setStatus({
            loading: false,
            enabled: false,
            modules: [],
            message: err?.message || "Could not load FAQ assistant status.",
            dailyOrgCap: 50,
            dailyUserCap: 50,
            maxQuestionLength: 1000,
            maxQuestionLineBreaks: 6,
            orgRemaining: 0,
            userRemaining: 0,
          });
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, staffId]);

  useEffect(() => {
    if (moduleName && modules.length > 0 && !modules.includes(moduleName)) {
      setModuleName("");
    }
  }, [modules, moduleName]);

  useEffect(() => {
    onSessionChange?.({ module: moduleName, messages });
  }, [moduleName, messages, onSessionChange]);

  useEffect(() => {
    if (status.loading) return;
    onUsageChange?.({
      enabled: status.enabled,
      dailyOrgCap: status.dailyOrgCap,
      dailyUserCap: status.dailyUserCap,
      orgRemaining: status.orgRemaining,
      userRemaining: status.userRemaining,
    });
  }, [
    status.loading,
    status.enabled,
    status.dailyOrgCap,
    status.dailyUserCap,
    status.orgRemaining,
    status.userRemaining,
    onUsageChange,
  ]);

  const capReached = status.orgRemaining <= 0 || status.userRemaining <= 0;
  const canSend =
    !!moduleName && draft.trim() && !sending && status.enabled && !capReached;

  const handleModuleChange = useCallback((value) => {
    setModuleName(value);
    setMessages([]);
    setDraft("");
    setLocalError("");
    setSupportDraft(null);
    setSupportMessage("");
  }, []);

  const handleSend = useCallback(
    async (questionOverride) => {
      // Only treat real string overrides as questions (ignore click events).
      const question =
        typeof questionOverride === "string"
          ? questionOverride.trim()
          : draft.trim();
      if (
        !moduleName ||
        !question ||
        sending ||
        !status.enabled ||
        capReached
      ) {
        return;
      }
      const rejected = validateFaqAiQuestion(question, {
        maxLength: status.maxQuestionLength,
        maxLineBreaks: status.maxQuestionLineBreaks,
      });
      if (rejected) {
        setLocalError(rejected);
        return;
      }
      setLocalError("");
      setDraft("");
      setSupportDraft(null);
      setSupportMessage("");
      setSending(true);
      setMessages((prev) => [...prev, { role: "user", text: question }]);
      pushFaqRecent({
        id: buildAskRecentId(moduleName, question),
        type: "ask",
        title: question,
        module: moduleName,
        mainModule: moduleName,
        chatSessionId: chatSessionId || "",
      });
      try {
        const result = await askPlatformFaq({
          organizationId,
          staffId,
          module: moduleName,
          question,
          chatSessionId,
        });
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: result.answer,
            tokens: result.tokens,
            followUps: result.followUps || [],
            requestId: result.requestId || undefined,
            wasHelpful: undefined,
          },
        ]);
        setStatus((prev) => ({
          ...prev,
          orgRemaining: Math.max(0, (prev.orgRemaining || 0) - 1),
          userRemaining: Math.max(0, (prev.userRemaining || 0) - 1),
        }));
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: err?.message || "Could not get an answer.",
            tokens: 0,
            error: true,
          },
        ]);
        try {
          await loadStatus();
        } catch {
          /* keep local remaining */
        }
      } finally {
        setSending(false);
      }
    },
    [
      draft,
      organizationId,
      staffId,
      moduleName,
      chatSessionId,
      sending,
      status.enabled,
      status.maxQuestionLength,
      status.maxQuestionLineBreaks,
      capReached,
      loadStatus,
    ],
  );

  const openSupportDraftForMessage = useCallback(
    (assistantMsg, messageIndex) => {
      let questionText = "";
      for (let i = messageIndex - 1; i >= 0; i -= 1) {
        if (messages[i]?.role === "user") {
          questionText = messages[i].text || "";
          break;
        }
      }
      setSupportMessage("");
      setSupportDraft({
        requestId: assistantMsg.requestId,
        module: moduleName,
        question: questionText,
        answer: assistantMsg?.text || "",
        whyNot: "",
      });
    },
    [messages, moduleName],
  );

  const handleFeedback = useCallback(
    async (requestId, wasHelpful, messageIndex) => {
      const id = String(requestId || "").trim();
      if (!id || feedbackBusyId === id) return;
      const existing = messages.find(
        (msg) => msg.role === "assistant" && msg.requestId === id,
      );
      if (existing && typeof existing.wasHelpful === "boolean") return;

      // Not helpful: open note modal first. Nothing is saved until Send note.
      if (!wasHelpful) {
        if (existing) openSupportDraftForMessage(existing, messageIndex);
        return;
      }

      setFeedbackBusyId(id);
      setLocalError("");
      try {
        const result = await setFaqAiFeedback({
          organizationId,
          staffId,
          requestId: id,
          wasHelpful: true,
        });
        setMessages((prev) =>
          prev.map((msg) =>
            msg.role === "assistant" && msg.requestId === id
              ? { ...msg, wasHelpful: result.wasHelpful }
              : msg,
          ),
        );
        onFeedbackChange?.({
          chatSessionId,
          requestId: id,
          wasHelpful: result.wasHelpful,
        });
        if (supportDraft?.requestId === id) {
          setSupportDraft(null);
          setSupportMessage("");
        }
      } catch (err) {
        setLocalError(err?.message || "Could not save feedback.");
      } finally {
        setFeedbackBusyId("");
      }
    },
    [
      feedbackBusyId,
      messages,
      organizationId,
      staffId,
      chatSessionId,
      onFeedbackChange,
      openSupportDraftForMessage,
      supportDraft,
    ],
  );

  const closeSupportDraft = useCallback(() => {
    if (supportSubmitting) return;
    setSupportDraft(null);
    setSupportMessage("");
  }, [supportSubmitting]);

  const handleSupportSubmit = useCallback(async () => {
    if (!supportDraft || supportSubmitting) return;
    const why = String(supportDraft.whyNot || "").trim();
    if (!why) {
      setSupportMessage("Please add why this was not helpful.");
      supportNoteRef.current?.focus();
      return;
    }
    // Subject + full ticket body are built silently — not shown in the UI.
    const ticket = buildSupportTicketDraft({
      moduleName: supportDraft.module,
      question: supportDraft.question,
      answer: supportDraft.answer,
      whyNot: why,
    });
    setSupportSubmitting(true);
    setSupportMessage("");
    try {
      await setFaqAiFeedback({
        organizationId,
        staffId,
        requestId: supportDraft.requestId,
        wasHelpful: false,
        feedbackReason: why,
      });
      const result = await submitFeedbackToClickUp({
        inquiryType: "AQ Assist – Not helpful",
        module: supportDraft.module || "Other",
        description: ticket.description,
        taskName: `Support: AQ Assist – Not helpful – ${
          why.length > 60 ? `${why.slice(0, 60)}…` : why
        }`,
        screenshots: [],
      });
      if (!result?.success) {
        throw new Error(
          result?.message ||
            result?.error ||
            "Could not send your support request.",
        );
      }
      // Update chat + recent-rail icon only when closing — avoids “stuck” feel
      // where the thumbs-down appears while the modal is still open.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.role === "assistant" && msg.requestId === supportDraft.requestId
            ? { ...msg, wasHelpful: false, feedbackReason: why }
            : msg,
        ),
      );
      onFeedbackChange?.({
        chatSessionId,
        requestId: supportDraft.requestId,
        wasHelpful: false,
      });
      setSupportDraft(null);
      setSupportMessage("");
    } catch (err) {
      setSupportMessage(
        err?.message || "Could not send. Please try again.",
      );
    } finally {
      setSupportSubmitting(false);
    }
  }, [
    supportDraft,
    supportSubmitting,
    organizationId,
    staffId,
    chatSessionId,
    onFeedbackChange,
  ]);

  useEffect(() => {
    if (!supportDraft) return undefined;
    const t = window.setTimeout(() => supportNoteRef.current?.focus(), 40);
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!supportSubmitting) {
          setSupportDraft(null);
          setSupportMessage("");
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [supportDraft, supportSubmitting]);

  const supportWhyReady = !!String(supportDraft?.whyNot || "").trim();

  if (status.loading) {
    return (
      <div
        className={`faq-ask faq-ask-loading${
          variant === "modal" ? " faq-ask--modal" : ""
        }`}
        aria-busy="true"
        aria-live="polite"
      >
        <div className="faq-ask-loading-inner">
          <span className="faq-ask-loading-icon" aria-hidden="true">
            <FaqAssistMark bordered />
            <FaqAssistAiIcon />
          </span>
          <p className="faq-ask-loading-title">Loading assistant…</p>
          <p className="faq-ask-loading-sub">Please wait a moment</p>
        </div>
      </div>
    );
  }

  if (!status.enabled) {
    return (
      <div className="faq-ask-disabled">
        <p>
          {status.message ||
            "FAQ assistant is not enabled for this organisation."}
        </p>
        <p className="faq-ask-hint">
          Browse the published FAQs on this page, or use Contact support from
          the Help menu.
        </p>
      </div>
    );
  }

  return (
    <>
    <div className={`faq-ask${variant === "modal" ? " faq-ask--modal" : ""}`}>
      {modulePicker === "dropdown" ? (
        <div className="faq-ask-toolbar">
          <div className="faq-ask-toolbar-start">
            {moduleName ? (
              <p className="faq-ask-meta">
                Today: {status.orgRemaining} of {status.dailyOrgCap} organisation
                asks left · {status.userRemaining} of {status.dailyUserCap} for
                you
              </p>
            ) : null}
          </div>
          <div className="faq-ask-toolbar-field">
            <label className="faq-ask-label" htmlFor="faq-ask-module">
              Module
            </label>
            <select
              id="faq-ask-module"
              className="faq-ask-select"
              value={moduleName}
              onChange={(e) => handleModuleChange(e.target.value)}
              disabled={modules.length === 0}
            >
              <option value="">
                {modules.length === 0
                  ? "No modules available"
                  : "Select a module to start"}
              </option>
              {modules.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      {!moduleName ? (
        <div className="faq-ask-empty">
          <div className="faq-ask-empty-icon" aria-hidden="true">
            <FaqAssistMark bordered />
            <FaqAssistAiIcon className="faq-ask-empty-ai" />
          </div>
          <p className="faq-ask-empty-title">
            {modulePicker === "hidden"
              ? "Choose a module on the right to start"
              : "Choose a module to start"}
          </p>
          <p className="faq-ask-hint">
            Choose a module on the right to get started.
          </p>
        </div>
      ) : (
        <>
          <div className="faq-ask-thread" aria-live="polite" ref={threadRef}>
            {messages.length === 0 ? (
              <div className="faq-ask-empty faq-ask-empty--inline">
                <p className="faq-ask-empty-title">Ask how to use {moduleName}</p>
                <p className="faq-ask-hint">
                  Ask a clear question about how to use {moduleName}.
                </p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={`${msg.role}-${index}`}
                  ref={
                    index === messages.length - 1
                      ? lastBubbleRef
                      : undefined
                  }
                  className={`faq-ask-bubble faq-ask-bubble--${msg.role}${
                    msg.error ? " is-error" : ""
                  }`}
                >
                  {msg.role === "assistant" && !msg.error ? (
                    <FaqAskAnswerBody text={msg.text} />
                  ) : (
                    <p>{msg.text}</p>
                  )}
                  {msg.role === "assistant" && !msg.error && msg.tokens > 0 ? (
                    <span className="faq-ask-tokens">{msg.tokens} tokens</span>
                  ) : null}
                  {msg.role === "assistant" &&
                  !msg.error &&
                  msg.followUps?.length ? (
                    <div className="faq-ask-followups">
                      <span className="faq-ask-followups-label">
                        Follow-up questions
                      </span>
                      <div className="faq-ask-followups-list">
                        {msg.followUps.map((ask) => (
                          <button
                            key={ask}
                            type="button"
                            className="faq-ask-followup-btn"
                            disabled={sending || capReached}
                            onClick={() => handleSend(ask)}
                          >
                            {ask}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {msg.role === "assistant" && !msg.error && msg.requestId ? (
                    <div className="faq-ask-feedback">
                      <span className="faq-ask-feedback-label">
                        {typeof msg.wasHelpful === "boolean"
                          ? "Thanks for your feedback"
                          : "Was this helpful?"}
                      </span>
                      <div className="faq-ask-feedback-actions">
                        <button
                          type="button"
                          className={`faq-ask-feedback-btn${
                            msg.wasHelpful === true ? " is-selected" : ""
                          }`}
                          disabled={
                            feedbackBusyId === msg.requestId ||
                            typeof msg.wasHelpful === "boolean" ||
                            !!supportDraft
                          }
                          aria-pressed={msg.wasHelpful === true}
                          aria-label="Helpful"
                          title={
                            typeof msg.wasHelpful === "boolean"
                              ? "Feedback already saved"
                              : "Helpful"
                          }
                          onClick={() =>
                            handleFeedback(msg.requestId, true, index)
                          }
                        >
                          <i className="fas fa-thumbs-up" aria-hidden="true" />
                          <span>Yes</span>
                        </button>
                        <button
                          type="button"
                          className={`faq-ask-feedback-btn${
                            msg.wasHelpful === false ||
                            supportDraft?.requestId === msg.requestId
                              ? " is-selected is-no"
                              : ""
                          }`}
                          disabled={
                            feedbackBusyId === msg.requestId ||
                            typeof msg.wasHelpful === "boolean" ||
                            !!supportDraft
                          }
                          aria-pressed={
                            msg.wasHelpful === false ||
                            supportDraft?.requestId === msg.requestId
                          }
                          aria-label="Not helpful"
                          title={
                            typeof msg.wasHelpful === "boolean"
                              ? "Feedback already saved"
                              : "Not helpful"
                          }
                          onClick={() =>
                            handleFeedback(msg.requestId, false, index)
                          }
                        >
                          <i className="fas fa-thumbs-down" aria-hidden="true" />
                          <span>No</span>
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))
            )}
            {sending ? (
              <div
                className="faq-ask-typing"
                role="status"
                aria-live="polite"
                aria-label="Generating response"
              >
                <span className="faq-ask-typing-avatar" aria-hidden="true">
                  <FaqAssistMark />
                </span>
                <div className="faq-ask-typing-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            ) : null}
          </div>
          {capReached ? (
            <p className="faq-ask-hint">
              Today&apos;s FAQ assistant limit has been reached. Try again
              tomorrow or contact the AQ team.
            </p>
          ) : null}
          {localError ? <p className="faq-ask-error">{localError}</p> : null}
          <div className="faq-ask-composer" data-tour="faq-assist-composer">
            <div className="faq-ask-composer-shell">
              <textarea
                className="faq-ask-input"
                rows={variant === "modal" ? 2 : 3}
                maxLength={status.maxQuestionLength}
                placeholder={`How do I… in ${moduleName}?`}
                value={draft}
                disabled={sending || capReached}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (localError) setLocalError("");
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData?.getData("text") || "";
                  const next = `${draft}${pasted}`;
                  const rejected = validateFaqAiQuestion(next, {
                    maxLength: status.maxQuestionLength,
                    maxLineBreaks: status.maxQuestionLineBreaks,
                  });
                  if (rejected) {
                    e.preventDefault();
                    setLocalError(rejected);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <div className="faq-ask-composer-footer">
                <span className="faq-ask-count">
                  {draft.length} / {status.maxQuestionLength}
                </span>
                <button
                  type="button"
                  className={`faq-ask-send${sending ? " is-sending" : ""}`}
                  disabled={!canSend}
                  onClick={() => handleSend()}
                  aria-label={sending ? "Generating response" : "Send"}
                  title={sending ? "Generating…" : "Send"}
                >
                  <i
                    className={`fas ${sending ? "fa-spinner fa-spin" : "fa-wand-magic-sparkles"}`}
                    aria-hidden="true"
                  />
                  <span>{sending ? "Generating…" : "Send"}</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    {supportDraft
      ? createPortal(
          <div
            className="faq-ask-support-modal-backdrop"
            role="presentation"
            onClick={closeSupportDraft}
          >
            <div
              className="faq-ask-support-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="faq-ask-support-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                id="faq-ask-support-modal-title"
                className="faq-ask-support-modal-title"
              >
                Why was this not helpful?
              </h3>
              <textarea
                id="faq-ask-support-note"
                ref={supportNoteRef}
                className="faq-ask-support-note-input"
                rows={5}
                maxLength={500}
                value={supportDraft.whyNot}
                placeholder="What was wrong or missing?"
                aria-describedby="faq-ask-support-note-hint"
                disabled={supportSubmitting}
                onChange={(e) => {
                  const whyNot = e.target.value;
                  setSupportDraft((prev) =>
                    prev ? { ...prev, whyNot } : prev,
                  );
                  if (supportMessage) setSupportMessage("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && supportWhyReady) {
                    e.preventDefault();
                    handleSupportSubmit();
                  }
                }}
              />
              <p id="faq-ask-support-note-hint" className="faq-ask-support-hint">
                A short note is required so support can follow up.
              </p>
              {supportMessage ? (
                <p
                  className={`faq-ask-support-msg${
                    supportMessage === "Sent to support." ? " is-ok" : " is-warn"
                  }`}
                >
                  {supportMessage}
                </p>
              ) : null}
              <div className="faq-ask-support-actions">
                <button
                  type="button"
                  className="faq-ask-support-cancel"
                  disabled={supportSubmitting}
                  onClick={closeSupportDraft}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="faq-ask-support-submit"
                  disabled={supportSubmitting || !supportWhyReady}
                  title={
                    supportWhyReady
                      ? "Save note and send to support"
                      : "Add a note to send"
                  }
                  onClick={handleSupportSubmit}
                >
                  {supportSubmitting ? "Sending…" : "Send note"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null}
    </>
  );
}

export default FaqAskPanel;
